import React, { useEffect, useMemo, useState } from "react";
import { X, Users, Search, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Create / edit a teacher group. Picks members from the roster (multi-select).
 * `initial` — null = create mode, existing group object = edit mode.
 */
export default function GroupFormModal({ initial, roster, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [selected, setSelected] = useState(new Set(initial?.student_ids || []));
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // Lock background scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const filtered = useMemo(() => {
    // Only students can be group members — hide any teachers/admins the
    // roster endpoint returned.
    const base = roster.filter((s) => !s.role || s.role === "student");
    const t = q.toLowerCase();
    if (!t) return base;
    return base.filter((s) =>
      (s.email || "").toLowerCase().includes(t) ||
      (s.name || "").toLowerCase().includes(t)
    );
  }, [roster, q]);

  const toggle = (userId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setErr("Group name is required");
      return;
    }
    setErr("");
    setSaving(true);
    const payload = {
      name: name.trim(),
      description: description.trim(),
      student_ids: Array.from(selected),
    };
    try {
      if (isEdit) {
        await api.patch(`/staff/groups/${initial.group_id}`, payload);
      } else {
        await api.post("/staff/groups", payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to save group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="group-form-modal" onClick={onClose}>
      <div
        className="bg-bone w-full max-w-2xl rounded-3xl border border-border overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-border bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-sage/10 text-sage flex items-center justify-center">
              <Users className="w-4.5 h-4.5" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">
                {isEdit ? "Edit group" : "New group"}
              </div>
              <div className="text-xs text-muted-foreground">
                {isEdit ? "Update members, name, or description" : "Group students to assign work and track progress"}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-secondary text-muted-foreground" data-testid="group-form-close">
            <X className="w-4 h-4 mx-auto" />
          </button>
        </header>

        <div className="px-6 py-5 overflow-y-auto">
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Spring 2026 cohort — Group A"
                data-testid="group-form-name"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage"
                maxLength={120}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional note — classroom, schedule, focus area…"
                data-testid="group-form-description"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:border-sage min-h-[60px]"
                maxLength={500}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                  Members ({selected.size} selected)
                </label>
              </div>
              <div className="relative mb-2">
                <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search roster…"
                  data-testid="group-form-search"
                  className="w-full ps-10 pe-4 py-2 rounded-xl border border-border bg-white text-sm focus:outline-none focus:border-sage"
                />
              </div>
              <div
                className="border border-border rounded-xl bg-white divide-y divide-border max-h-[36vh] overflow-y-auto"
                data-testid="group-form-roster"
              >
                {filtered.length === 0 && (
                  <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                    {roster.length === 0 ? "No students have signed in yet" : "No match"}
                  </div>
                )}
                {filtered.map((s) => {
                  const on = selected.has(s.user_id);
                  return (
                    <button
                      key={s.user_id}
                      type="button"
                      onClick={() => toggle(s.user_id)}
                      data-testid={`group-form-toggle-${s.user_id}`}
                      className={`w-full text-start px-4 py-2.5 flex items-center gap-3 hover:bg-bone transition ${on ? "bg-sage/5" : ""}`}
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${on ? "bg-sage border-sage text-white" : "border-border bg-white"}`}>
                        {on && <CheckCircle2 className="w-3 h-3" />}
                      </div>
                      {s.picture ? (
                        <img src={s.picture} alt="" className="w-7 h-7 rounded-full" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-sage/10 text-sage text-[11px] font-semibold flex items-center justify-center">
                          {(s.name || s.email || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{s.name || "—"}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{s.email}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {err && <div className="text-xs text-terracotta" data-testid="group-form-error">{err}</div>}
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-border bg-white flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-sage" data-testid="group-form-cancel">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="group-form-save"
            className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark disabled:opacity-60 text-white font-medium px-5 py-2 rounded-lg transition-all"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create group"}
          </button>
        </footer>
      </div>
    </div>
  );
}
